import { loading, success, failure } from 'utilities/actions'
import { put, take, takeLatest, all, fork } from 'redux-saga/effects'

import * as actions from './actions'

import {
  API_URL,
  ENROLMENT_ENTITLEMENTS_ENDPOINT,
  ENROLMENT_ENTITLEMENTS_PAGE_SIZE,
} from 'qap/constants'
import { requestActions } from 'state/request'
import { stringify } from 'utilities/apiQueryString'

// The entitlements list is paginated invisibly, the same way participants are:
// the page renders every account and program it is given, so a partial first
// page would silently drop accounts. We keep fetching until a short page signals
// the end and accumulate the lot.
//
// Each page goes through AUTHED_REQUEST (rather than calling fetch directly) so
// 401/token-invalid responses are routed to expireSession and failures get the
// app's global error handling. AUTHED_REQUEST is fire-and-forget, so we await
// each page's resolution with `take` before requesting the next.
export function* fetchEnrolmentEntitlements(): Generator<any, any, any> {
  yield put({ type: loading(actions.API_ENROLMENT_ENTITLEMENTS_INDEX) })

  let offset = 0
  let rows: any[] = []

  while (true) {
    const queryString = stringify({
      page: { offset, limit: ENROLMENT_ENTITLEMENTS_PAGE_SIZE },
    })
    const url = `${API_URL}/${ENROLMENT_ENTITLEMENTS_ENDPOINT}?${queryString}`

    yield put({
      type: requestActions.AUTHED_REQUEST,
      method: 'GET',
      url,
      cancelKey: 'enrolmentEntitlements.index',
      hideLoading: true,
      successAction: success(actions.API_ENROLMENT_ENTITLEMENTS_PAGE),
      failureAction: failure(actions.API_ENROLMENT_ENTITLEMENTS_PAGE),
    })

    // Wait for this page to resolve. A token-invalid 401 never resolves here:
    // the request middleware dispatches expireSession instead, logging the user out.
    const response = yield take([
      success(actions.API_ENROLMENT_ENTITLEMENTS_PAGE),
      failure(actions.API_ENROLMENT_ENTITLEMENTS_PAGE),
    ])

    if (response.type === failure(actions.API_ENROLMENT_ENTITLEMENTS_PAGE)) {
      yield put({
        type: failure(actions.API_ENROLMENT_ENTITLEMENTS_INDEX),
        errors: response.errors?.length ? response.errors : ['Failed to load'],
      })
      return
    }

    const pageData = response.data || []
    rows = rows.concat(pageData)

    if (pageData.length < ENROLMENT_ENTITLEMENTS_PAGE_SIZE) break
    offset += ENROLMENT_ENTITLEMENTS_PAGE_SIZE
  }

  yield put({
    type: success(actions.API_ENROLMENT_ENTITLEMENTS_INDEX),
    data: rows,
    total: rows.length,
  })
}

function* watchFetch(): Generator<any, any, any> {
  yield takeLatest(actions.API_ENROLMENT_ENTITLEMENTS_INDEX, fetchEnrolmentEntitlements)
}

export default function* enrolmentEntitlementsSagas(): Generator<any, any, any> {
  yield all([
    fork(watchFetch)
  ])
}
